/**
 * own-photo.ts — domain schema for the "own photo" upload flow.
 *
 * A shopper uploads their own image (instead of picking a catalog wallpaper),
 * configures it via the same material/size/crop primitives, and adds it to
 * the cart as a `source: 'own_photo'` line. The original file is persisted
 * server-side (via a signed-upload URL minted by `api`, see
 * `schemas/api/own-photo.ts`) to a content-addressable path in the PRIVATE
 * `wandero-private` bucket — `own-photo/{uuid}.{ext}` — so the cart/order
 * snapshot can reference it after reload (unlike the local
 * `URL.createObjectURL(file)` preview, which dies once the tab/session ends).
 * The bucket is private (customer photos are PII): client-facing previews
 * resolve a short-lived signed-read URL via `POST /v1/own-photo/read-url`, and
 * no public object URL exists for the bucket (sec-review FINDING #1, T11/T12).
 *
 * Source: 2026-07-18-custom-photo-order-page spec
 * §"Cart/order snapshot: аддитивный own_photo вариант".
 */

import { z } from 'zod';
import { WANDERO_PRIVATE_BUCKET } from '../util/image-url.js';

/**
 * Reference to an uploaded original stored in the private `wandero-private`
 * bucket. Required on `own_photo` cart/order lines (see `cart.ts` `CartItem`,
 * `orders.ts` `OrderItemSnapshot`), absent on `catalog` lines.
 */
export const OwnPhotoOriginalRef = z.object({
  /** Always `'wandero-private'` — the dedicated PRIVATE own-photo bucket
   * (customer photos are PII; no public read policy). Reads go through a
   * short-lived signed URL from `POST /v1/own-photo/read-url` (T11/T12). */
  bucket: z.literal(WANDERO_PRIVATE_BUCKET),
  /** `own-photo/{uuid}.{ext}` — load-bearing for fulfillment; never rewritten. */
  path: z.string().min(1),
  /** Original filename as picked by the shopper, for display only. */
  filename: z.string().max(260),
  /** MIME type as reported at upload time (validated server-side too). */
  content_type: z.string(),
  size_bytes: z.number().int().positive(),
});
export type OwnPhotoOriginalRef = z.infer<typeof OwnPhotoOriginalRef>;
